import React, { useState, useEffect, useCallback } from 'react';
import http from '../services/httpClient';
import './StructuralActionLogModal.css';

const formatDateTime = (raw) => {
  const d = new Date(raw);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date} · ${time}`;
};

/**
 * Item Mapping Logs — read-only viewer for every recipe-ingredient mapping event, with
 * timestamp, date and the acting user's email. Purely informational (no revert); the
 * structural changes (rename/unit/merge) have their own revertable log.
 */
const ItemMappingLogModal = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setError('');
    try {
      const response = await http.get('/recipes/item-mapping-logs');
      setLogs(response && Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch item mapping logs:', err);
      setError('Failed to fetch item mapping logs.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchLogs(false);
  }, [isOpen, fetchLogs]);

  if (!isOpen) return null;

  const actorLabel = (log) => log.user_email || log.user_name || (log.user_id ? `User #${log.user_id}` : 'Unknown user');

  const ACTION_BADGE = {
    mapped_ingredients: 'MAPPED INGREDIENTS',
    created_item: 'CREATED ITEM',
    edited_item: 'EDITED ITEM',
    removed_item: 'REMOVED ITEM',
    status_change: 'STATUS CHANGE',
    // Structured Recipe Book. Added, never replacing — every value above renders
    // exactly as it did before, and an unknown action still falls through to the
    // humanising default below.
    recipe_book_edit: 'RECIPE BOOK EDIT',
    recipe_book_removal: 'RECIPE BOOK REMOVAL',
    preparation_edit: 'PREPARATION EDIT',
    complimentary_edit: 'COMPLIMENTARY EDIT',
  };
  const badgeFor = (a) => ACTION_BADGE[a] || String(a || 'ACTION').replace(/_/g, ' ').toUpperCase();

  // The source chip. `details.source` is absent on every row written before the Recipe
  // Book existed, and absent means Item Mapping — so nothing was backfilled and old
  // cards keep their meaning.
  const sourceFor = (log) => (log.details?.source === 'recipe_book' ? 'via Recipe Book' : 'via Item Mapping');

  const RECIPE_BOOK_ACTIONS = new Set([
    'recipe_book_edit', 'recipe_book_removal', 'preparation_edit', 'complimentary_edit',
  ]);

  const ENTITY_TAG = { preparation: 'Preparation', complimentary: 'Complimentary' };

  const fmt = (v) => (Number.isFinite(Number(v)) ? String(Number(Number(v).toFixed(4))) : String(v ?? '—'));

  /** A Recipe Book card: summary line, then before → after, then removals, then blast radius. */
  const renderRecipeBookBody = (log) => {
    const d = log.details || {};
    const updates = Array.isArray(d.updates) ? d.updates : [];
    const removals = Array.isArray(d.removals) ? d.removals : [];
    const affected = Array.isArray(d.affected_menu_items) ? d.affected_menu_items : [];

    const batchChange = updates.find((u) => u.field === 'batch_quantity');
    const quantityChanges = updates.filter((u) => u.field !== 'batch_quantity');

    const summary = [
      batchChange ? 'Batch changed' : null,
      quantityChanges.length ? `${quantityChanges.length} quantit${quantityChanges.length === 1 ? 'y' : 'ies'} updated` : null,
      removals.length ? `${removals.length} ingredient${removals.length === 1 ? '' : 's'} removed` : null,
      affected.length ? `affects ${affected.length} menu item${affected.length === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(' · ');

    return (
      <>
        <div className="log-sub-details-clean">{summary || 'No changes recorded'}</div>

        {batchChange && (
          <div className="log-sub-details-clean" style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
            Batch: {fmt(batchChange.old)} → <strong>{fmt(batchChange.new)}</strong>
          </div>
        )}

        {quantityChanges.length > 0 && (
          <div className="log-sub-details-clean" style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
            Updated: {quantityChanges.map((u) => `${u.name} ${fmt(u.old)} → ${fmt(u.new)}`).join(', ')}
          </div>
        )}

        {removals.length > 0 && (
          <div className="log-sub-details-clean" style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626' }}>
            Removed: {removals.map((r) => `${r.name} — ${fmt(r.value)}`).join(', ')}
          </div>
        )}

        {affected.length > 0 && (
          <div className="log-sub-details-clean" style={{ marginTop: '4px', fontSize: '12px', color: '#e67e22' }}>
            Affected: {affected.map((m) => m.name).join(', ')}
          </div>
        )}
      </>
    );
  };

  // Per-action summary line + change rows.
  const renderBody = (log) => {
    const d = log.details || {};
    if (RECIPE_BOOK_ACTIONS.has(log.action)) return renderRecipeBookBody(log);
    switch (log.action) {
      case 'edited_item': {
        const c = d.changes || {};
        return (
          <>
            {c.name && (
              <div className="log-details flex-details">
                <div className="from-side"><strong>Name from:</strong> {c.name.from ?? '—'}</div>
                <div className="to-side"><strong>To:</strong> {c.name.to ?? '—'}</div>
              </div>
            )}
            {c.price && (
              <div className="log-sub-details-clean" style={{ marginTop: '4px' }}>
                Price: ₹{c.price.from ?? '—'} → <strong>₹{c.price.to ?? '—'}</strong>
              </div>
            )}
            {c.servings && (
              <div className="log-sub-details-clean" style={{ marginTop: '4px' }}>
                Servings: {c.servings.from ?? '—'} → <strong>{c.servings.to ?? '—'}</strong>
              </div>
            )}
            {!c.name && !c.price && !c.servings && (
              <div className="log-sub-details-clean">Item details updated</div>
            )}
          </>
        );
      }
      case 'created_item':
        return (
          <div className="log-sub-details-clean">
            New item created{d.price != null ? ` · ₹${d.price}` : ''}{d.servings != null ? ` · ${d.servings} serving${d.servings === 1 ? '' : 's'}` : ''}{log.ingredient_count ? ` · ${log.ingredient_count} ingredient${log.ingredient_count === 1 ? '' : 's'}` : ''}
          </div>
        );
      case 'removed_item':
        return (
          <div className="log-sub-details-clean" style={{ color: '#dc2626' }}>
            Item {d.softDelete ? 'deactivated (has history)' : 'permanently removed'}
          </div>
        );
      case 'status_change':
        return (
          <div className="log-sub-details-clean">
            Status: <strong>{d.label || d.status || '—'}</strong>
          </div>
        );
      case 'mapped_ingredients':
      default: {
        const ingredients = Array.isArray(d.ingredients) ? d.ingredients : [];
        const created = Array.isArray(d.created) ? d.created : [];
        const preparations = Array.isArray(d.preparations) ? d.preparations : [];
        return (
          <>
            <div className="log-sub-details-clean">
              {log.ingredient_count} ingredient{log.ingredient_count === 1 ? '' : 's'} saved
              {log.created_count > 0 ? ` · ${log.created_count} new item${log.created_count === 1 ? '' : 's'} created` : ''}
            </div>
            {ingredients.length > 0 && (
              <div className="log-sub-details-clean" style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                Ingredients: {ingredients.join(', ')}
              </div>
            )}
            {created.length > 0 && (
              <div className="log-sub-details-clean" style={{ marginTop: '4px', fontSize: '12px', color: '#16a34a' }}>
                New items: {created.join(', ')}
              </div>
            )}
            {preparations.length > 0 && (
              <div className="log-sub-details-clean" style={{ marginTop: '4px', fontSize: '12px', color: '#e67e22' }}>
                Preparations: {preparations.join(', ')}
              </div>
            )}
          </>
        );
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content structural-log-modal">
        <div className="modal-header">
          <h2>Item Mapping Logs</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">

          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="structural-spinner-container">
              <div className="structural-spinner"></div>
              <div className="loading-text">Loading logs...</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="no-data">
              <div className="no-data-icon">📋</div>
              <p className="no-data-title">No Activity Yet</p>
              <p className="no-data-sub">Ingredient mapping actions will appear here as chefs map recipes.</p>
            </div>
          ) : (
            <div className="logs-list">
              {logs.map((log) => (
                <div key={log.log_id} className="log-card">
                  <div className="log-header">
                    <span className="action-type-badge">{badgeFor(log.action)}</span>
                    <span
                      className="log-source-chip"
                      style={{
                        marginLeft: '8px',
                        padding: '1px 7px',
                        borderRadius: '10px',
                        background: log.details?.source === 'recipe_book' ? '#eef2ff' : '#f1f5f9',
                        color: log.details?.source === 'recipe_book' ? '#3730a3' : '#475569',
                        fontSize: '10px',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {sourceFor(log)}
                    </span>
                    <span className="log-date">{formatDateTime(log.created_at)}</span>
                  </div>

                  <div className="log-details">
                    <div className="item-name-row" style={{ textAlign: 'left' }}>
                      <strong>Item:</strong>{' '}
                      {log.recipe_name || (log.details?.entity_name) || `#${log.recipe_id}`}
                      {ENTITY_TAG[log.details?.scope_kind] && (
                        <span
                          style={{
                            marginLeft: '6px',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            background: '#f5f2ec',
                            color: '#6b6459',
                            fontSize: '10px',
                          }}
                        >
                          {ENTITY_TAG[log.details.scope_kind]}
                        </span>
                      )}
                    </div>
                    <div className="item-name-row" style={{ textAlign: 'left' }}>
                      <strong>By:</strong> {actorLabel(log)}
                    </div>
                    {renderBody(log)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemMappingLogModal;
